import React, { useState } from 'react';
import {
  Form, FormGroup, Label, Input, Button,
  Alert, Progress, Table, Badge
} from 'reactstrap';
import { Download, Eye, AlertCircle, CheckCircle } from 'lucide-react';
import API from '../utils/api';

const SourceImport = ({ source, onClose }) => {
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(false);
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const response = await API.post(`/sources/${source.id}/import`, {
        preview,
        limit: preview ? 10 : limit
      });

      if (preview) {
        setPreviewData(response.data);
      } else {
        setResult(response.data);
      }
    } catch (err) {
      // Handle timeout specifically
      if (err.code === 'ECONNABORTED' || err.response?.status === 504) {
        setResult({
          success: true,
          background: true,
          message: "Import is running in the background due to large dataset. You can check the status by refreshing the sources list or checking the import history.",
          stats: { note: "Background processing in progress..." }
        });
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setImporting(false);
    }
  };

  const renderPreview = () => {
    if (!previewData || !previewData.preview) return null;

    return (
      <div className="mt-3">
        <h6 className="text-white">Preview Data</h6>
        <Table dark size="sm" responsive>
          <thead>
            <tr>
              <th>Domain</th>
              <th>Drop Time</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {previewData.preview.map((item, idx) => (
              <tr key={idx}>
                <td>{item.domain}</td>
                <td>
                  {item.drop_date ?
                    new Date(item.drop_date).toLocaleString() :
                    (item.expiration_date || '-')}
                </td>
                <td>${item.price || '0'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Alert color="info">
          Total records found: {previewData.stats?.total || 0}
        </Alert>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    const stats = result.stats || {};

    return (
      <div className="mt-3">
        <Alert color={result.success ? 'success' : 'danger'}>
          {result.success ? (
            <>
              <CheckCircle size={20} className="me-2" />
              Import completed successfully!
            </>
          ) : (
            <>
              <AlertCircle size={20} className="me-2" />
              Import failed: {result.error}
            </>
          )}
        </Alert>

        {result.success && (
          <div className="stats-grid">
            <div className="stat-item">
              <Label>Total Records</Label>
              <h4 className="text-white">{stats.total}</h4>
            </div>
            <div className="stat-item">
              <Label>Imported</Label>
              <h4 className="text-success">{stats.imported}</h4>
            </div>
            <div className="stat-item">
              <Label>Failed</Label>
              <h4 className="text-danger">{stats.failed}</h4>
            </div>
            <div className="stat-item">
              <Label>Duplicates</Label>
              <h4 className="text-warning">{stats.duplicates}</h4>
            </div>
          </div>
        )}

        {stats.errors && stats.errors.length > 0 && (
          <Alert color="warning" className="mt-3">
            <h6>Errors:</h6>
            <ul className="mb-0">
              {stats.errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Form onSubmit={(e) => { e.preventDefault(); handleImport(); }}>
      <Alert color="info">
        <strong>Source:</strong> {source.name}<br />
        <strong>Type:</strong> {source.type?.toUpperCase()}<br />
        <strong>Format:</strong> {source.data_format?.toUpperCase()}
      </Alert>

      <FormGroup>
        <Label className="text-white">Import Mode</Label>
        <div>
          <FormGroup check inline>
            <Label check className="text-white">
              <Input
                type="radio"
                name="mode"
                checked={!preview}
                onChange={() => setPreview(false)}
              />
              Full Import
            </Label>
          </FormGroup>
          <FormGroup check inline>
            <Label check className="text-white">
              <Input
                type="radio"
                name="mode"
                checked={preview}
                onChange={() => setPreview(true)}
              />
              Preview Only
            </Label>
          </FormGroup>
        </div>
      </FormGroup>

      {!preview && (
        <FormGroup>
          <Label for="limit" className="text-white">Record Limit</Label>
          <Input
            type="number"
            id="limit"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
            min="0"
            className="bg-dark text-white"
          />
          <small className="text-muted">Set to 0 for no limit</small>
        </FormGroup>
      )}

      {error && (
        <Alert color="danger">
          <AlertCircle size={20} className="me-2" />
          {error}
        </Alert>
      )}

      {renderPreview()}
      {renderResult()}

      <div className="mt-3">
        <Button
          color="primary"
          type="submit"
          disabled={importing}
        >
          {importing ? (
            <>Processing...</>
          ) : (
            <>
              {preview ? <Eye size={16} className="me-1" /> : <Download size={16} className="me-1" />}
              {preview ? 'Preview Data' : 'Start Import'}
            </>
          )}
        </Button>
        <Button
          color="secondary"
          className="ms-2"
          onClick={onClose}
          disabled={importing}
        >
          Close
        </Button>
      </div>

      <style jsx>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        .stat-item {
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 0.25rem;
          text-align: center;
        }
      `}</style>
    </Form>
  );
};

export default SourceImport;